// src/pages/Home.js
// MODERNIZED: Nulek brand colors (#E63946 red, #222222/#2E2E2E charcoal palette)
// CAROUSEL: Integrated nulek-about1.png, nulek-about2.png, and nulek-about3.png

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Home.module.css';

import planningImg   from '../images/services/planning-scheduling.jpg';
import riskImg       from '../images/services/schedule-risk.jpg';
import chainageImg   from '../images/services/time-chainage.jpg';
import reportingImg  from '../images/services/Dashboards.jpg';
import trainingImg   from '../images/services/training-workshop.jpg';

import about1 from '../images/about/About01.png';
import about2 from '../images/about/About02.png';
import about3 from '../images/about/About03.png';
import about4 from '../images/about/About04.png';
import about5 from '../images/about/About05.png';
import about6 from '../images/about/About06.png';
import about7 from '../images/about/About07.png';
import about8 from '../images/about/About08.png';
import about9 from '../images/about/About09.png';



const Home = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeHero, setActiveHero] = useState(0);
  const sectionRefs = useRef([]);
  const heroImages = [about1, about2, about3,about4,about5,about6,about7,about8,about9];

  // Carousel Logic
  useEffect(() => {
    const heroInterval = setInterval(() => {
      setActiveHero((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(heroInterval);
  }, [heroImages.length]);

  // Scroll-triggered animations & resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRefs.current.forEach(el => { if (el) observer.observe(el); });
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const services = [
    { title: 'Planning & Scheduling',     img: planningImg,  desc: 'End-to-end scheduling from EOI to closeout — ensuring clear, accurate, and defensible programmes.', path: '/services/PlanningScheduling', color: 'var(--color-primary)' },
    { title: 'Schedule Risk Analysis',      img: riskImg,      desc: 'Quantitative risk assessment using Monte Carlo (P20/P50/P80), sensitivity analysis, and contingency modelling.', path: '/services/ScheduleRiskAnalysis', color: 'var(--color-primary-dark)' },
    { title: 'Time–Chainage Diagrams',      img: chainageImg,  desc: 'High-impact linear scheduling using TILOS & Turbo-Chart for rail, roads, and utilities.', path: '/services/TimeChainage', color: 'var(--color-primary)' },
    { title: 'Power BI Reporting',          img: reportingImg, desc: 'Real-time EVM, portfolio, and lookahead dashboards that turn complex data into executive-ready insights.', path: '/services/Report', color: 'var(--color-primary-dark)' },
    { title: 'Training & Uplift',          img: trainingImg,  desc: 'Practical courses in Planning, EVM, and SRA — tailored for engineers, planners, and project managers.', path: '/services/training', color: 'var(--color-primary)' },
  ];

  const testimonials = [
    { quote: "Nulek's schedule risk analysis gave us the confidence to proceed with our $350M water project — their P50/P80 dates were spot-on.", author: "Director of Project Controls", company: "Major Australian Water Authority" },
    { quote: "The TILOS time–chainage diagrams transformed how we communicated sequencing to stakeholders on our 42km rail corridor.", author: "Project Director", company: "Rail Infrastructure Contractor" },
    { quote: "Their EVM dashboards became our board's single source of truth — no more conflicting reports across teams.", author: "Head of Project Governance", company: "Energy Infrastructure Provider" },
  ];

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Nulek Consulting | Project Planning & Logistics Experts Australia</title>
        <meta name="description" content="Nulek Consulting provides precision project planning, schedule risk analysis, and logistics solutions for complex infrastructure projects across Australia." />
      </Helmet>

      {/* ── HERO ── */}
      <section ref={el => sectionRefs.current[0] = el} className={styles.heroSection}>
        <div className={styles.heroAccentBar} />
        <div className={`${styles.heroContent} ${isMobile ? styles.heroContentMobile : ''}`}>
          {/* Text */}
          <div className={`${styles.heroText} ${isMobile ? styles.heroTextMobile : ''}`}>
            <div className={styles.heroBadge}>
              <span className={styles.heroBadgeDot} />
              <span className={styles.heroBadgeText}>Australian Planning Consultancy</span>
            </div>

            <h1 className={`${styles.heroTitle} ${isMobile ? styles.heroTitleMobile : ''}`}>
              Precision Planning for Complex Infrastructure
            </h1>
            <p className={`${styles.heroLead} ${isMobile ? styles.heroLeadMobile : ''}`}>
              An Australian-owned consultancy delivering end-to-end project controls — from tender to closeout.
            </p>
            <p className={`${styles.heroBody} ${isMobile ? styles.heroBodyMobile : ''}`}>
              We support building, water, rail, energy, and utilities projects with technically rigorous, contract-compliant, and <strong>defensible</strong> planning and scheduling.
            </p>
            <div className={`${styles.heroCta} ${isMobile ? styles.heroCtaMobile : ''}`}>
              <Link to="/contact" className={styles.heroButton}>
                Schedule a Consultation <span>→</span>
              </Link>
            </div>
          </div>

          {/* Carousel Image Container */}
          <div className={`${styles.heroImageWrapper} ${isMobile ? styles.heroImageWrapperMobile : ''}`}>
            <div className={styles.heroImageContainer}>
              <div className={styles.heroImageOverlay} />
              {heroImages.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt="Nulek Consulting"
                  className={`${styles.heroImage} ${activeHero === index ? styles.heroImageActive : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section ref={el => sectionRefs.current[2] = el} className={styles.servicesSection}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionEyebrow}>What We Do</p>
          <h2 className={`${styles.sectionTitle} ${isMobile ? styles.sectionTitleMobile : ''}`}>Our Core Services</h2>
        </div>

        <div className={`${styles.servicesGrid} ${isMobile ? styles.servicesGridMobile : ''}`}>
          {services.map((s, idx) => (
            <div key={idx} className={styles.serviceCard}>
              <div className={`${styles.serviceImageWrapper} ${isMobile ? styles.serviceImageWrapperMobile : ''}`}>
                <div className={styles.serviceImage} style={{ backgroundImage: `url(${s.img})` }} />
              </div>
              <div className={styles.serviceContent}>
                <div className={styles.serviceAccent} style={{ background: s.color }} />
                <h3 className={styles.serviceTitle}>{s.title}</h3>
                <p className={styles.serviceDesc}>{s.desc}</p>
                <Link to={s.path} className={styles.serviceLink}>Learn more →</Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section ref={el => sectionRefs.current[3] = el} className={styles.testimonialsSection}>
        <div className={styles.sectionHeader}>
          <h2 className={`${styles.testimonialsTitle} ${isMobile ? styles.testimonialsTitleMobile : ''}`}>Trusted by Industry Leaders</h2>
        </div>
        <div className={styles.testimonialCard}>
          <p className={`${styles.testimonialQuote} ${isMobile ? styles.testimonialQuoteMobile : ''}`}>
            "{testimonials[activeTestimonial].quote}"
          </p>
          <div className={styles.testimonialAuthor}>
            <div className={styles.authorName}>{testimonials[activeTestimonial].author}</div>
            <div className={styles.authorCompany}>{testimonials[activeTestimonial].company}</div>
          </div>
          <div className={styles.testimonialDots}>
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveTestimonial(i)}
                className={`${styles.dot} ${i === activeTestimonial ? styles.dotActive : ''}`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section ref={el => sectionRefs.current[4] = el} className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={`${styles.ctaTitle} ${isMobile ? styles.ctaTitleMobile : ''}`}>Let's Build Certainty Into Your Next Project</h2>
          <Link to="/contact" className={styles.ctaButton}>
            Book a Free 30-Minute Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;